/**
 * The key hierarchy.
 *
 * Four layers, each wrapping the one below, so that no single compromise opens
 * everything and so that ordinary events — a password change, a member
 * leaving, a lost account — are re-wrapping operations rather than
 * re-encrypting terabytes of media.
 *
 *   password ──PBKDF2──▶ password key
 *                            │ wraps
 *                            ▼
 *                    user private key  ◀── the public half is stored in clear
 *                            │ unwraps
 *                            ▼
 *                        group key ────wrapped once per member, and once
 *                            │          to the offline recovery key
 *                            │ wraps
 *                            ▼
 *                        file key ──── one per file, wrapping nothing else
 *
 * What each layer buys:
 *
 *   · The password key is derived, never stored. The server sees a password
 *     only at sign-in, for authentication, and never the key derived from it.
 *   · The user keypair means a group key can be handed to someone who is
 *     offline: anyone can wrap to a public key, only they can unwrap.
 *   · The group key means adding a member is one wrap, not re-encrypting the
 *     album; removing one is one delete.
 *   · A file key per file means a leaked key exposes one photo.
 *
 * ── The recovery key ──
 *
 * The group key is additionally wrapped to a public key whose private half is
 * never on a server — it lives offline, on a machine or a USB stick. That is
 * what makes the whole scheme survivable: without it, every member forgetting
 * their password destroys the album permanently.
 *
 * It is deliberately not usable by the running system. An attacker who takes
 * full control of the servers can make themselves an administrator, because
 * that is a row in a table they now own — but administration is an application
 * role and recovery is possession of a secret. Recovery therefore has to be an
 * offline operation: export the wrapped blob, decrypt it on your own machine,
 * re-wrap it to the person's new public key, upload the result. The server only
 * ever moves opaque bytes. A "recover" button that made the server decrypt
 * would hand that attacker everything, and is the one shape this must never
 * take.
 *
 * ── What this does not defend against ──
 *
 * An attacker in control of the server can serve modified JavaScript that
 * captures a password as it is typed. Data already stored stays unreadable to
 * them, but anyone who signs in afterwards is exposed. No browser application
 * escapes this, because the server delivers the code that performs the
 * cryptography. It is a reason to protect the server, not a reason to skip
 * this.
 */

import { MediaCryptoError, base64ToBytes, bytesToBase64 } from "./crypto.js";

/**
 * PBKDF2 iterations.
 *
 * PBKDF2 is used rather than Argon2id because it is the only password-based
 * derivation WebCrypto offers, and shipping a WASM Argon2 to every browser is a
 * cost this does not yet justify. 600 000 is the OWASP figure for
 * PBKDF2-HMAC-SHA256 and takes a few hundred milliseconds on a phone — slow
 * enough to matter to someone guessing, fast enough not to be felt at sign-in.
 *
 * Stored per user rather than assumed, so it can be raised later without
 * locking anyone out of an account created under the old value.
 */
export const PBKDF2_ITERATIONS = 600_000;

const SALT_BYTES = 16;
const IV_BYTES = 12;
const AES_BITS = 256;

/**
 * RSA-OAEP for wrapping to a public key.
 *
 * ECDH keys are smaller and generate faster, but wrapping to one needs an
 * ephemeral keypair and a derivation step every time. Wrapping to a public key
 * happens rarely here — joining a group, changing a password, escrowing — while
 * the frequent operation is wrapping a file key with the group key, which is
 * symmetric and fast either way. The simpler construction wins on the rare
 * path.
 *
 * 3072 bits rather than 2048: generation happens once per account and costs
 * about a second, which is affordable at registration, and it removes any
 * question about the margin lasting the life of the photos.
 */
const RSA_PARAMS: RsaHashedKeyGenParams = {
  name: "RSA-OAEP",
  modulusLength: 3072,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: "SHA-256",
};

/* ─────────────────────────── password-derived key ────────────────────────── */

export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_BYTES));
}

/**
 * Turn a password into a wrapping key.
 *
 * Never extractable, and never sent anywhere: it exists only to unwrap the
 * private key in the browser that derived it.
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: copy(salt), iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: AES_BITS },
    false,
    ["encrypt", "decrypt"],
  );
}

/* ───────────────────────────────── keypairs ──────────────────────────────── */

export interface SerializedKeypair {
  /** SPKI, base64. Stored in clear — it is public by design. */
  publicKey: string;
  /** PKCS#8 sealed under the password key, base64. Useless without it. */
  wrappedPrivateKey: string;
}

/**
 * A keypair for one person, with the private half already sealed.
 *
 * The private key is never returned unsealed: a caller that cannot supply the
 * password key has no legitimate use for it, and an API that hands it back
 * invites it being stored somewhere it should not be.
 */
export async function generateKeypair(passwordKey: CryptoKey): Promise<SerializedKeypair> {
  const pair = await crypto.subtle.generateKey(RSA_PARAMS, true, ["encrypt", "decrypt"]);
  const spki = new Uint8Array(await crypto.subtle.exportKey("spki", pair.publicKey));
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));

  return {
    publicKey: bytesToBase64(spki),
    wrappedPrivateKey: bytesToBase64(await sealWithAes(pkcs8, passwordKey)),
  };
}

/** Only ever succeeds in a context that knows the password. */
export async function unwrapPrivateKey(
  wrappedPrivateKey: string,
  passwordKey: CryptoKey,
): Promise<CryptoKey> {
  const pkcs8 = await openWithAes(base64ToBytes(wrappedPrivateKey), passwordKey);
  return crypto.subtle.importKey("pkcs8", copy(pkcs8), RSA_PARAMS, false, ["decrypt"]);
}

export async function importPublicKey(publicKey: string): Promise<CryptoKey> {
  try {
    return await crypto.subtle.importKey("spki", copy(base64ToBytes(publicKey)), RSA_PARAMS, false, [
      "encrypt",
    ]);
  } catch {
    throw new MediaCryptoError("Not a usable public key.");
  }
}

/**
 * Re-seal a private key under a new password without changing the key itself.
 *
 * This is what makes a password change cheap: identity and access are unchanged
 * because the keypair is unchanged, so nothing that was wrapped to this person
 * has to be rewritten.
 */
export async function rewrapPrivateKey(
  wrappedPrivateKey: string,
  oldPasswordKey: CryptoKey,
  newPasswordKey: CryptoKey,
): Promise<string> {
  const pkcs8 = await openWithAes(base64ToBytes(wrappedPrivateKey), oldPasswordKey);
  return bytesToBase64(await sealWithAes(pkcs8, newPasswordKey));
}

/* ──────────────────────────── group and file keys ────────────────────────── */

/** Raw bytes, because these are wrapped and stored far more often than used. */
export function generateSymmetricKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(AES_BITS / 8));
}

/** Hand a symmetric key to someone who is not here, using only their public half. */
export async function wrapToPublicKey(key: Uint8Array, publicKey: CryptoKey): Promise<string> {
  const sealed = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, copy(key));
  return bytesToBase64(new Uint8Array(sealed));
}

export async function unwrapFromPrivateKey(
  wrapped: string,
  privateKey: CryptoKey,
): Promise<Uint8Array> {
  try {
    const raw = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      copy(base64ToBytes(wrapped)),
    );
    return new Uint8Array(raw);
  } catch {
    // A key wrapped to somebody else, or a tampered value. The caller must not
    // be able to tell which.
    throw new MediaCryptoError("Could not unwrap that key.");
  }
}

/** Wrap a file key with a group key. The common case, and the fast one. */
export async function wrapWithKey(key: Uint8Array, wrappingKey: Uint8Array): Promise<string> {
  return bytesToBase64(await sealWithAes(key, await importAes(wrappingKey)));
}

export async function unwrapWithKey(wrapped: string, wrappingKey: Uint8Array): Promise<Uint8Array> {
  return openWithAes(base64ToBytes(wrapped), await importAes(wrappingKey));
}

/**
 * Wrap a key so that a passphrase — or an invite code — opens it.
 *
 * The pair below is what carries a group key to somebody who does not have an
 * account yet. The code travels in the invite link and is stored on this side
 * only as a hash, so the server holds a sealed key and no way to open it, while
 * the person following the link holds the one thing that does.
 */
export async function wrapWithDerivedKey(key: Uint8Array, derived: CryptoKey): Promise<string> {
  return bytesToBase64(await sealWithAes(key, derived));
}

export async function unwrapWithDerivedKey(
  wrapped: string,
  derived: CryptoKey,
): Promise<Uint8Array> {
  return openWithAes(base64ToBytes(wrapped), derived);
}

/* ─────────────────────────────── internals ───────────────────────────────── */

async function importAes(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== AES_BITS / 8) throw new MediaCryptoError("Key must be 32 bytes.");
  return crypto.subtle.importKey("raw", copy(raw), "AES-GCM", false, ["encrypt", "decrypt"]);
}

/** Nonce prepended, so a sealed value is always one self-contained blob. */
async function sealWithAes(plaintext: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, copy(plaintext)),
  );
  const out = new Uint8Array(iv.length + sealed.length);
  out.set(iv, 0);
  out.set(sealed, iv.length);
  return out;
}

async function openWithAes(blob: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
  if (blob.length <= IV_BYTES) throw new MediaCryptoError("Sealed value is too short.");
  try {
    const raw = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: copy(blob.subarray(0, IV_BYTES)) },
      key,
      copy(blob.subarray(IV_BYTES)),
    );
    return new Uint8Array(raw);
  } catch {
    throw new MediaCryptoError("Wrong key, or the sealed value has been altered.");
  }
}

/**
 * WebCrypto takes an ArrayBuffer, and a view may cover part of a larger one —
 * passing `.buffer` straight through would quietly include its neighbours.
 */
function copy(view: Uint8Array): ArrayBuffer {
  return view.slice().buffer as ArrayBuffer;
}
